import { LoanDashboardFormCard } from "../LoanDashboardForm";
import { formatAmount } from "../../../utils";
import { LoanApplication, LOSPlatform } from "../../../contexts";
import { FC, useMemo } from "react";
import {
  LoanApplicationCaseStatus,
  LoanApplicationCaseStatusDashboardMappingToText,
  LoanApplicationStatus,
  LoanApplicationStatusDashboardMappingToText,
  LoanProductType,
} from "../../../hooks/api/types";
import { LoanDashboardProgress } from "../LoanDashboardProgress";

type LoanDetailsProps = {
  loan: LoanApplication;
};

export const LoanDetails: FC<LoanDetailsProps> = ({ loan }) => {
  const { amount, paidAmount, remainingAmount, lateFee } = useMemo(() => {
    return {
      amount: loan.amount ?? 0,
      paidAmount: loan.paidAmount ?? 0,
      remainingAmount: loan.remainingAmount ?? 0,
      lateFee: loan?.lateFee ?? "-",
    };
  }, [loan?.amount, loan?.paidAmount, loan?.remainingAmount]);

  const completion = useMemo(() => {
    if (!paidAmount) return 100;
    if (!remainingAmount) return 0;
    return (
      100 -
      Math.round((paidAmount / (paidAmount + remainingAmount)) * 10000) / 100
    );
  }, [amount, paidAmount, remainingAmount]);

  const loanInput = useMemo(() => {
    if (loan?.loanType === LoanProductType.FIXED_TERM) {
      return loan?.fixedTermLoan;
    } else if (loan?.loanType === LoanProductType.INVOICE_DISCOUNTING) {
      return loan?.invoiceDiscountingLoan?.invoices?.[0];
    } else if (loan?.loanType === LoanProductType.PAYABLE_FINANCING) {
      return loan?.payableFinancingLoan?.invoices?.[0];
    } else if (loan?.loanType === LoanProductType.REVENUE_BASED_FINANCING) {
      return loan?.revenueBasedLoan;
    } else if (loan?.loanType === LoanProductType.POS_REVENUE_BASED_FINANCING) {
      return loan?.posRevenueBasedLoan;
    } else if (loan?.loanType === LoanProductType.BNPL) {
      return loan?.buyNowPayLaterLoan;
    } else if (
      loan?.loanType === LoanProductType.LOC_INVOICE_DISCOUNTING ||
      loan?.loanType === LoanProductType.LOC_PAYABLE_FINANCING
    ) {
      return loan?.creditLine;
    }
    return 0;
  }, [loan]);

  const loanAmount = useMemo(() => {
    if (
      loan?.loanType === LoanProductType.LOC_INVOICE_DISCOUNTING ||
      loan?.loanType === LoanProductType.LOC_PAYABLE_FINANCING
    ) {
      return loan?.creditLine?.requestedLimit;
    }
    if (
      (loan?.approvedAmount || loan?.amount) &&
      loan?.losPlatform === LOSPlatform.CREDIBLEX
    ) {
      return loan?.approvedAmount ? loan.approvedAmount : loan.amount;
    }
    return loanInput?.loanAmount;
  }, [loan, loanInput]);

  const showProgress = useMemo(() => {
    return (
      loan.status === LoanApplicationStatus.ACTIVE ||
      loan.status === LoanApplicationStatus.WAITING_FOR_DISBURSEMENT ||
      loan.caseStatus === LoanApplicationCaseStatus.ACTIVE ||
      loan.caseStatus === LoanApplicationCaseStatus.WAITING_FOR_DISBURSEMENT ||
      loan.caseStatus === LoanApplicationCaseStatus.SEND_TO_SERVICING ||
      loan.caseStatus === LoanApplicationCaseStatus.RESTRUCTURED
    );
  }, [loan]);

  return (
    <div className="sm:cx-flex cx-flex-wrap cx-items-center cx-py-4 cx-px-2 sm:cx-gap-x-4 sm:cx-divide-x sm:cx-divide-neutral-light">
      <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
        <LoanDashboardFormCard
          label={
            loan.losPlatform === LOSPlatform.CREDIBLEX && loan.caseStatus
              ? LoanApplicationCaseStatusDashboardMappingToText[loan.caseStatus]
              : LoanApplicationStatusDashboardMappingToText[loan.status] ??
                "Requested Amount"
          }
        >
          <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
            <div className="cx-text-text-primary cx-font-medium cx-text-xl">
              <div className="cx-font-aed-font"> D </div>
            </div>
            <div className="cx-text-text-primary cx-font-bold cx-text-xl">
              {formatAmount(loanAmount * (loanInput?.discountRate ?? 1))}
            </div>
          </div>
        </LoanDashboardFormCard>
      </div>

      {showProgress && (
        <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
          <LoanDashboardFormCard label="Outstanding amount">
            <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
              <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                <div className="cx-font-aed-font"> D </div>
              </div>
              <div className="cx-text-text-primary cx-font-bold cx-text-xl">
                {formatAmount(remainingAmount)}
              </div>
            </div>
          </LoanDashboardFormCard>
        </div>
      )}

      {loan.status !== LoanApplicationStatus.ACTIVE &&
        loan.caseStatus !== LoanApplicationCaseStatus.ACTIVE &&
        loan.caseStatus !== LoanApplicationCaseStatus.EARLY_CLOSURE &&
        loan.caseStatus !== LoanApplicationCaseStatus.FORCED_CLOSURE && (
          <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
            <LoanDashboardFormCard label="Duration">
              <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
                <div className="cx-text-text-primary cx-font-bold cx-text-xl">
                  {loanInput?.duration}
                </div>
                <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                  {loanInput?.durationType}
                </div>
              </div>
            </LoanDashboardFormCard>
          </div>
        )}

      {showProgress &&
        lateFee != null &&
        loan.caseStatus == LoanApplicationCaseStatus.PAST_MATURITY && (
          <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
            <LoanDashboardFormCard label="Late fee">
              <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
                <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                  <div className="cx-font-aed-font"> D </div>
                </div>
                <div className="cx-text-text-primary cx-font-bold cx-text-xl">
                  {lateFee != "-" ? formatAmount(lateFee as number) : "-"}
                </div>
              </div>
            </LoanDashboardFormCard>
          </div>
        )}

      {showProgress && (
        <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-justify-center cx-py-3 cx-px-4">
          <LoanDashboardProgress value={completion} label="Complete" />
        </div>
      )}
    </div>
  );
};

export default LoanDetails;
